<template>
  <div class="prompt-panel">
    <div class="panel-header">
      <h5 class="mb-0">
        <i class="fa fa-lightbulb me-2"></i>
        Prompt Variables
      </h5>
    </div>

    <!-- Search Bar -->
    <div class="search-section">
      <div class="input-group">
        <span class="input-group-text">
          <i class="fa fa-search"></i>
        </span>
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="Search categories or variables..."
          @input="handleSearch"
        />
        <button
          v-if="searchQuery"
          class="btn btn-outline-secondary"
          type="button"
          @click="clearSearch"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading categories...</p>
    </div>

    <!-- Error State -->
    <div v-if="!isLoading && error" class="error-state">
      <i class="fa fa-exclamation-triangle text-danger fa-2x mb-2"></i>
      <p class="text-danger">{{ error }}</p>
      <button class="btn btn-sm btn-primary" @click="loadCategories">
        <i class="fa fa-refresh me-1"></i>
        Retry
      </button>
    </div>

    <!-- Categories List -->
    <div v-if="!isLoading && !error" class="categories-list">
      <!-- No Results -->
      <div v-if="filteredCategories.length === 0" class="no-results">
        <i class="fa fa-search fa-2x text-muted mb-2"></i>
        <p class="text-muted">No categories found</p>
      </div>

      <!-- Category Items -->
      <div
        v-for="category in filteredCategories"
        :key="category._id"
        class="category-item"
      >
        <div
          class="category-header"
          :class="{ active: expandedCategories.has(category._id) }"
          draggable="true"
          @dragstart="handleCategoryDragStart(category, $event)"
          @click="toggleCategory(category._id)"
        >
          <div class="category-title">
            <i
              class="fa me-2"
              :class="
                expandedCategories.has(category._id)
                  ? 'fa-chevron-down'
                  : 'fa-chevron-right'
              "
            ></i>
            <span>{{ category.title }}</span>
          </div>
          <span
            class="badge bg-primary"
            v-if="category.variableCount !== undefined"
          >
            {{ category.variableCount }}
          </span>
        </div>

        <!-- Variables List (Collapsible) -->
        <div
          v-if="expandedCategories.has(category._id)"
          class="variables-container"
        >
          <!-- Loading Variables -->
          <div
            v-if="loadingVariables.has(category._id)"
            class="variables-loading"
          >
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
            <small class="text-muted ms-2">Loading variables...</small>
          </div>

          <!-- Variables Error -->
          <div
            v-else-if="variableErrors.has(category._id)"
            class="variables-error"
          >
            <small class="text-danger">
              <i class="fa fa-exclamation-circle me-1"></i>
              {{ variableErrors.get(category._id) }}
            </small>
          </div>

          <!-- Variables List -->
          <div
            v-else-if="categoryVariables.get(category._id)?.length"
            class="variables-list"
          >
            <div
              v-for="variable in categoryVariables.get(category._id)"
              :key="variable._id"
              class="variable-item"
              draggable="true"
              @dragstart="handleDragStart(variable, $event)"
            >
              <div class="variable-header">
                <i class="fa fa-grip-vertical drag-handle me-2"></i>
                <div class="variable-content">
                  <div class="variable-title">{{ variable.title }}</div>
                  <div v-if="variable.description" class="variable-description">
                    {{ variable.description }}
                  </div>
                </div>
              </div>
              <div class="variable-meta">
                <span v-if="variable.slug" class="badge bg-secondary text-xs">
                  {{ variable.slug }}
                </span>
              </div>
            </div>
          </div>

          <!-- No Variables -->
          <div v-else class="no-variables">
            <small class="text-muted">
              <i class="fa fa-info-circle me-1"></i>
              No variables in this category
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import apiService from "@/services/apiService";

// Props
const props = defineProps<{
  isActive?: boolean;
}>();

// Types
interface Category {
  _id: string;
  title: string;
  variableCount?: number;
}

interface Variable {
  _id: string;
  prompt_category_id: string;
  title: string;
  description: string;
  type_id: string;
  icon: string | null;
  slug: string;
  filterable: boolean;
  visible_on_card: boolean;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: string | null;
  deleted_by: string | null;
  is_trash: boolean;
  __v: number;
}

// State
const categories = ref<Category[]>([]);
const categoryVariables = ref<Map<string, Variable[]>>(new Map());
const expandedCategories = ref<Set<string>>(new Set());
const loadingVariables = ref<Set<string>>(new Set());
const variableErrors = ref<Map<string, string>>(new Map());
const isLoading = ref(false);
const error = ref<string | null>(null);
const searchQuery = ref("");

// Computed
const filteredCategories = computed(() => {
  if (!searchQuery.value.trim()) {
    return categories.value;
  }

  const query = searchQuery.value.toLowerCase();
  return categories.value.filter((category) => {
    // Search in category title
    if (category.title.toLowerCase().includes(query)) {
      return true;
    }

    // Search in variables if loaded
    const variables = categoryVariables.value.get(category._id);
    if (variables) {
      return variables.some(
        (variable) =>
          variable.title.toLowerCase().includes(query) ||
          variable.description?.toLowerCase().includes(query) ||
          variable.slug?.toLowerCase().includes(query)
      );
    }

    return false;
  });
});

// Methods
const loadCategories = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await apiService.get("/common/prompt-categories");

    // Handle new response structure with data property
    if (response && response.data && Array.isArray(response.data)) {
      categories.value = response.data;
    } else if (Array.isArray(response)) {
      // Fallback for old response structure
      categories.value = response;
    } else {
      categories.value = [];
    }
  } catch (err: any) {
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load categories";
  } finally {
    isLoading.value = false;
  }
};

const loadVariables = async (categoryId: string) => {
  loadingVariables.value.add(categoryId);
  variableErrors.value.delete(categoryId);

  try {
    const response = await apiService.get(
      `/common/prompt-variables-filter?prompt_category_id=${categoryId}`
    );

    // Handle new response structure with data property
    let variables: Variable[] = [];
    if (response && response.data && Array.isArray(response.data)) {
      variables = response.data;
    } else if (Array.isArray(response)) {
      // Fallback for old response structure
      variables = response;
    }

    categoryVariables.value.set(categoryId, variables);

    // Update category variable count
    const category = categories.value.find((c) => c._id === categoryId);
    if (category) {
      category.variableCount = variables.length;
    }
  } catch (err: any) {
    const errorMsg =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load variables";
    variableErrors.value.set(categoryId, errorMsg);
  } finally {
    loadingVariables.value.delete(categoryId);
  }
};

const toggleCategory = async (categoryId: string) => {
  if (expandedCategories.value.has(categoryId)) {
    expandedCategories.value.delete(categoryId);
  } else {
    expandedCategories.value.add(categoryId);

    // Load variables if not already loaded
    if (
      !categoryVariables.value.has(categoryId) &&
      !loadingVariables.value.has(categoryId)
    ) {
      await loadVariables(categoryId);
    }
  }
};

const handleSearch = () => {
  // Search is handled by computed property
};

const clearSearch = () => {
  searchQuery.value = "";
};

const handleDragStart = (variable: Variable, event: DragEvent) => {
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/json", JSON.stringify(variable));
    event.dataTransfer.setData("text/plain", variable.title);
    event.dataTransfer.setData("item-type", "variable");
  }
};

const handleCategoryDragStart = async (
  category: Category,
  event: DragEvent
) => {
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "copy";

    // Load variables if not already loaded
    if (!categoryVariables.value.has(category._id)) {
      await loadVariables(category._id);
    }

    const variables = categoryVariables.value.get(category._id) || [];

    const categoryData = {
      category: category,
      variables: variables,
    };

    event.dataTransfer.setData(
      "application/json",
      JSON.stringify(categoryData)
    );
    event.dataTransfer.setData("text/plain", category.title);
    event.dataTransfer.setData("item-type", "category");
  }
};

// Lifecycle
onMounted(() => {
  loadCategories();
});

// Watch isActive prop to reload categories when tab becomes visible
watch(
  () => props.isActive,
  (newVal, oldVal) => {
    if (newVal && !oldVal && categories.value.length === 0) {
      loadCategories();
    }
  }
);
</script>

<style scoped>
.prompt-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
  height: 100%;
  flex: 1;
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.panel-header h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
}

.search-section {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.search-section .input-group-text {
  background: #fff;
  border-right: none;
}

.search-section .form-control {
  border-left: none;
}

.search-section .form-control:focus {
  box-shadow: none;
  border-color: #ced4da;
}

.loading-state,
.error-state,
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
}

.categories-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

.category-item {
  border-bottom: 1px solid #f0f0f0;
}

.category-header {
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;
  user-select: none;
}

.category-header:hover {
  background-color: #f8f9fa;
}

.category-header.active {
  background-color: #e7f3ff;
}

.category-title {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #495057;
  flex: 1;
}

.category-title i {
  font-size: 0.75rem;
  transition: transform 0.2s;
}

.variables-container {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.variables-loading,
.variables-error,
.no-variables {
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.variables-list {
  padding: 0.5rem 0;
}

.variable-item {
  padding: 0.75rem 1rem;
  margin: 0.25rem 0.5rem;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: move;
  transition: all 0.2s;
}

.variable-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

.variable-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.drag-handle {
  color: #adb5bd;
  cursor: grab;
  margin-top: 2px;
}

.variable-item:active .drag-handle {
  cursor: grabbing;
}

.variable-content {
  flex: 1;
}

.variable-title {
  font-weight: 500;
  color: #212529;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.variable-description {
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.variable-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.text-xs {
  font-size: 0.7rem;
}

/* Scrollbar Styling */
.categories-list::-webkit-scrollbar {
  width: 6px;
}

.categories-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.categories-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.categories-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
